import { useCallback, useEffect, useState } from "react";
import { ArrowDown, ArrowUp, Plus } from "lucide-react";
import { toast } from "sonner";
import { Switch } from "@/components/ui/switch";
import { adminApi, api, errMsg } from "@/lib/api";
import { CURRENCIES, formatPlanPrice } from "@/lib/plans";
import { bulkSummary, countOf } from "@/pages/admin/AdminData";

const VALID = /^\s*(\d[\d,]*(\.\d+)?|\.\d+)\s*$/;
const NUM = /^\s*\d+(\.\d+)?\s*$/;
const init = (p) => ({
  name: p.name, tagline: p.tagline || "", features: (p.features || []).join("\n"), popular: !!p.popular, hidden: !!p.hidden, custom: !!p.custom, online_store: p.online_store ?? ["growth", "business", "enterprise"].includes(p.code), reports: p.reports ?? ["growth", "business", "enterprise"].includes(p.code),
  users: p.limits?.users ?? "", branches: p.limits?.branches ?? "", prices: { ...p.prices }, yearly_discount: p.yearly_discount || "0", yearly_overrides: { ...(p.yearly_overrides || {}) },
});

function PlanEditor({ plan, first, last, onMove, reload, picked, onPick }) {
  const [v, setV] = useState(init(plan));
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);
  useEffect(() => setV(init(plan)), [plan]);
  const set = (k) => (e) => setV({ ...v, [k]: e?.target ? e.target.value : e });
  const save = async () => {
    setError("");
    if (!v.custom && CURRENCIES.some((c) => !VALID.test(String(v.prices[c] ?? "")))) return setError("Enter a valid price");
    if (Object.values(v.yearly_overrides).some((x) => x && !VALID.test(String(x)))) return setError("Enter a valid price");
    if (!NUM.test(String(v.yearly_discount)) || Number(v.yearly_discount) > 100) return setError("Enter a valid number");
    if ([v.users, v.branches].some((x) => x !== "" && !/^\s*\d+\s*$/.test(String(x)))) return setError("Enter a valid number");
    setBusy(true);
    try {
      await adminApi.put(`/plans/${plan.code}`, {
        name: v.name, tagline: v.tagline, features: v.features.split("\n"), popular: v.popular, hidden: v.hidden, custom: v.custom, online_store: v.online_store, reports: v.reports,
        limits: { users: v.users === "" ? null : String(v.users), branches: v.branches === "" ? null : String(v.branches) },
        prices: v.custom ? {} : v.prices, yearly_discount: String(v.yearly_discount), yearly_overrides: v.yearly_overrides,
      });
      toast.success(`${v.name} saved`);
      reload();
    } catch (e) {
      setError(errMsg(e));
    } finally {
      setBusy(false);
    }
  };
  const del = async (op) => {
    if (op === "delete" && !window.confirm(`Delete the ${plan.name} plan?`)) return;
    try {
      await (op === "delete" ? adminApi.delete(`/plans/${plan.code}`) : adminApi.post(`/plans/${plan.code}/restore`));
      reload();
    } catch (e) {
      toast.error(errMsg(e));
    }
  };
  const inp = "h-10 w-full rounded-lg border border-slate-200 px-3 text-sm outline-none focus:border-slate-900";
  const gone = !!plan.deleted_at;
  return (
    <div className={`rounded-2xl border bg-white p-5 ${gone ? "border-dashed opacity-60" : "border-slate-200"}`} data-testid={`admin-plan-${plan.code}`}>
      <div className="flex flex-wrap items-center gap-2">
        <input type="checkbox" checked={picked} onChange={onPick} aria-label={`Select ${plan.name}`} data-testid={`admin-plan-select-${plan.code}`} className="h-4 w-4 accent-slate-900" />
        <div className="font-display text-lg font-bold">{plan.name}{plan.popular && <span className="ml-2 rounded-full bg-amber-500 px-2 py-0.5 font-mono text-[10px] text-slate-950">Most popular</span>}{gone && <span className="ml-2 font-mono text-xs text-rose-600">deleted</span>}</div>
        <div className="ml-auto flex gap-1">
          <button disabled={first} onClick={() => onMove(-1)} className="grid h-8 w-8 place-items-center rounded-lg border border-slate-200 disabled:opacity-30" data-testid={`admin-plan-up-${plan.code}`} aria-label="Move up"><ArrowUp className="h-4 w-4" /></button>
          <button disabled={last} onClick={() => onMove(1)} className="grid h-8 w-8 place-items-center rounded-lg border border-slate-200 disabled:opacity-30" data-testid={`admin-plan-down-${plan.code}`} aria-label="Move down"><ArrowDown className="h-4 w-4" /></button>
          <button onClick={() => del(gone ? "restore" : "delete")} className={`h-8 rounded-lg px-3 text-xs font-bold ${gone ? "bg-emerald-600 text-white" : "border border-rose-200 text-rose-700"}`} data-testid={`admin-plan-${gone ? "restore" : "delete"}-${plan.code}`}>{gone ? "Restore" : "Delete"}</button>
        </div>
      </div>
      {!gone && (
        <div className="mt-4 grid gap-4 lg:grid-cols-2">
          <div className="space-y-2">
            <input className={inp} value={v.name} onChange={set("name")} placeholder="Name" data-testid={`admin-plan-name-${plan.code}`} />
            <input className={inp} value={v.tagline} onChange={set("tagline")} placeholder="Tagline" data-testid={`admin-plan-tagline-${plan.code}`} />
            <textarea className={`${inp} h-28 py-2`} value={v.features} onChange={set("features")} placeholder="One feature per line" data-testid={`admin-plan-features-${plan.code}`} />
            <div className="flex flex-wrap gap-4 text-sm font-semibold">
              <label className="flex items-center gap-2"><Switch checked={v.popular} onCheckedChange={set("popular")} data-testid={`admin-plan-popular-${plan.code}`} />Most popular</label>
              <label className="flex items-center gap-2"><Switch checked={v.hidden} onCheckedChange={set("hidden")} data-testid={`admin-plan-hidden-${plan.code}`} />Hidden</label>
              <label className="flex items-center gap-2"><Switch checked={v.custom} onCheckedChange={set("custom")} data-testid={`admin-plan-custom-${plan.code}`} />Custom / Contact sales</label>
              <label className="flex items-center gap-2"><Switch checked={v.online_store} onCheckedChange={set("online_store")} data-testid={`admin-plan-online-store-${plan.code}`} />Online store included</label>
              <label className="flex items-center gap-2"><Switch checked={v.reports} onCheckedChange={set("reports")} data-testid={`admin-plan-reports-${plan.code}`} />Reports included</label>
            </div>
            <div className="grid grid-cols-2 gap-2">
              <input className={inp} value={v.users} onChange={set("users")} placeholder="Users (blank = Unlimited)" data-testid={`admin-plan-limit-users-${plan.code}`} />
              <input className={inp} value={v.branches} onChange={set("branches")} placeholder="Branches (blank = Unlimited)" data-testid={`admin-plan-limit-branches-${plan.code}`} />
            </div>
          </div>
          <div className="space-y-2">
            <label className="flex items-center gap-2 text-sm font-semibold">Yearly discount %
              <input className={`${inp} w-24`} value={v.yearly_discount} onChange={set("yearly_discount")} data-testid={`admin-plan-discount-${plan.code}`} /></label>
            <div className="grid grid-cols-[48px_1fr_1fr_auto] items-center gap-2 font-mono text-xs">
              <span /><span className="text-slate-500">Monthly</span><span className="text-slate-500">Yearly override</span><span className="text-slate-500">Yearly</span>
              {CURRENCIES.map((c) => (
                <div key={c} className="contents">
                  <span className="font-bold">{c}</span>
                  <input className={inp} disabled={v.custom} value={v.prices[c] ?? ""} onChange={(e) => setV({ ...v, prices: { ...v.prices, [c]: e.target.value } })} data-testid={`admin-plan-input-${plan.code}-${c}`} />
                  <input className={inp} disabled={v.custom} value={v.yearly_overrides[c] ?? ""} placeholder="auto" onChange={(e) => setV({ ...v, yearly_overrides: { ...v.yearly_overrides, [c]: e.target.value } })} data-testid={`admin-plan-yearly-${plan.code}-${c}`} />
                  <span data-testid={`admin-plan-yearly-computed-${plan.code}-${c}`}>{plan.custom ? "—" : formatPlanPrice(plan.yearly_prices?.[c], c)}</span>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}
      {error && <div className="mt-3 rounded-lg bg-rose-50 px-3 py-2 text-sm font-medium text-rose-700" data-testid={`admin-plan-error-${plan.code}`}>{error}</div>}
      {!gone && <button onClick={save} disabled={busy} className="mt-4 h-10 rounded-xl bg-slate-900 px-5 text-sm font-bold text-white disabled:opacity-50" data-testid={`admin-plan-save-${plan.code}`}>Save plan</button>}
    </div>
  );
}

function AddonEditor({ code = "inventory", name = "Inventory add-on" }) {
  const [prices, setPrices] = useState({});
  useEffect(() => { api.get("/plans/addons").then((r) => setPrices(r.data.find((a) => a.code === code)?.prices || {})); }, [code]);
  const save = async () => {
    try {
      const { data } = await adminApi.put(`/addons/${code}`, { prices });
      setPrices(data.prices);
      toast.success(`${name} price saved`);
    } catch (e) {
      toast.error(errMsg(e));
    }
  };
  return (
    <div className="rounded-2xl border border-amber-300 bg-amber-50/50 p-5" data-testid={`admin-addon-${code}`}>
      <div className="font-display text-lg font-bold">{name} <span className="text-sm font-semibold text-slate-500">optional extra · blank = Contact sales</span></div>
      <div className="mt-3 flex flex-wrap gap-2">
        {CURRENCIES.map((c) => (
          <label key={c} className="flex items-center gap-1.5 font-mono text-xs font-bold">{c}
            <input value={prices[c] ?? ""} onChange={(e) => setPrices({ ...prices, [c]: e.target.value })} placeholder="contact" className="h-10 w-24 rounded-lg border border-slate-200 bg-white px-2 text-sm" data-testid={code === "inventory" ? `admin-addon-price-${c}` : `admin-addon-${code}-price-${c}`} />
          </label>
        ))}
        <button onClick={save} className="h-10 rounded-xl bg-slate-900 px-4 text-sm font-bold text-white" data-testid={code === "inventory" ? "admin-addon-save" : `admin-addon-${code}-save`}>Save add-on price</button>
      </div>
    </div>
  );
}

export default function AdminPlans() {
  const [plans, setPlans] = useState([]);
  const [showDeleted, setShowDeleted] = useState(false);
  const [sel, setSel] = useState([]);
  useEffect(() => setSel([]), [showDeleted]);
  const load = useCallback(() => adminApi.get("/plans").then((r) => setPlans(r.data)).catch((e) => toast.error(errMsg(e))), []);
  useEffect(() => { load(); }, [load]);
  const live = plans.filter((p) => !p.deleted_at);
  const move = async (i, d) => {
    const codes = live.map((p) => p.code);
    [codes[i], codes[i + d]] = [codes[i + d], codes[i]];
    setPlans((await adminApi.post("/plans/reorder", { codes })).data);
  };
  const add = async () => {
    try {
      await adminApi.post("/plans", { name: "New plan", tagline: "", features: [], prices: Object.fromEntries(CURRENCIES.map((c) => [c, "0"])), hidden: true });
      load();
    } catch (e) {
      toast.error(errMsg(e));
    }
  };
  const shown = showDeleted ? plans : live;
  const gone = (c) => !!plans.find((p) => p.code === c)?.deleted_at;
  const allOn = shown.length > 0 && shown.every((p) => sel.includes(p.code));
  const bulk = async (action) => {
    const ids = sel.filter((c) => (action === "restore" ? gone(c) : !gone(c)));
    if (!window.confirm(`${action === "delete" ? "Delete" : "Restore"} ${countOf(ids.length, "plan", "plans")}?`)) return;
    try {
      const { data: r } = await adminApi.post("/plans/bulk", { action, ids });
      (r.skipped ? toast.warning : toast.success)(bulkSummary(r, action === "delete" ? "deleted" : "restored"));
      setSel([]);
      load();
    } catch (e) {
      toast.error(errMsg(e));
    }
  };
  const nLive = sel.filter((c) => !gone(c)).length;
  const nGone = sel.length - nLive;
  return (
    <div className="space-y-4" data-testid="admin-plans">
      <div className="flex flex-wrap items-center gap-3">
        <p className="text-sm text-slate-500">The landing page and lock screen read these plans. Limits are shown to customers but not enforced.</p>
        <label className="ml-auto flex items-center gap-2 text-sm font-semibold"><Switch checked={showDeleted} onCheckedChange={setShowDeleted} data-testid="admin-plans-show-deleted" />Show deleted</label>
        <button onClick={add} className="flex h-10 items-center gap-1.5 rounded-xl bg-amber-500 px-4 text-sm font-bold text-slate-950" data-testid="admin-plan-add"><Plus className="h-4 w-4" /> Add plan</button>
      </div>
      <AddonEditor />
      <AddonEditor code="reports" name="Reports add-on" />
      <div className="flex flex-wrap items-center gap-3 rounded-xl border border-slate-200 bg-white px-4 py-2.5 text-sm" data-testid="admin-plans-bulk-bar">
        <label className="flex items-center gap-2 font-semibold"><input type="checkbox" checked={allOn} onChange={() => setSel(allOn ? [] : shown.map((p) => p.code))} data-testid="admin-plans-select-all" className="h-4 w-4 accent-slate-900" />Select all</label>
        <span className="font-mono text-xs text-slate-500" data-testid="admin-plans-selected-count">{sel.length} selected</span>
        {nLive > 0 && <button onClick={() => bulk("delete")} className="ml-auto h-8 rounded-lg bg-rose-600 px-4 text-xs font-bold text-white" data-testid="admin-plans-bulk-delete">Delete {nLive}</button>}
        {nGone > 0 && <button onClick={() => bulk("restore")} className={`${nLive ? "" : "ml-auto "}h-8 rounded-lg bg-emerald-600 px-4 text-xs font-bold text-white`} data-testid="admin-plans-bulk-restore">Restore {nGone}</button>}
      </div>
      {shown.map((p) => {
        const i = live.findIndex((x) => x.code === p.code);
        return <PlanEditor key={p.code} plan={p} first={i <= 0} last={i < 0 || i === live.length - 1} onMove={(d) => move(i, d)} reload={load} picked={sel.includes(p.code)} onPick={() => setSel((s) => (s.includes(p.code) ? s.filter((x) => x !== p.code) : [...s, p.code]))} />;
      })}
    </div>
  );
}
